import { Injectable, inject } from '@angular/core';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';

export interface ActivityFieldChange {
  fieldPath: string;
  changeKind: 'added' | 'removed' | 'changed';
  oldValue?: unknown;
  newValue?: unknown;
  valueType?: string;
}

export interface ActivityEvent {
  id: string;
  agencyId?: string;
  entityType: string;
  entityId: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  eventType?: string;
  actorUserId?: string;
  /** Present when the API returns it; used to label agency/system actors. */
  actorType?: string;
  source: 'server-api' | 'firestore-cdc' | 'backfill';
  occurredAt: string;
  summary?: string;
  fieldChanges: ActivityFieldChange[];
}

export interface ActivityLogResponse {
  events: ActivityEvent[];
  nextCursor?: {
    occurredAt: string;
    id: string;
  };
}

export interface ActivityLogFilters {
  entityType?: string;
  entityId?: string;
  actorUserId?: string;
  action?: 'CREATE' | 'UPDATE' | 'DELETE';
  source?: 'server-api' | 'firestore-cdc' | 'backfill';
  cursorOccurredAt?: string;
  cursorId?: string;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class ActivityLogService {
  private readonly api = inject(EnrollhereApiService);

  listAgencyActivity(
    agencyId: string,
    filters: ActivityLogFilters = {}
  ): Promise<ActivityLogResponse> {
    return this.api.get<ActivityLogResponse>(
      `/agency/${encodeURIComponent(agencyId)}/activity${this.toQuery(filters)}`
    );
  }

  listGlobalActivity(
    filters: ActivityLogFilters & { agencyId?: string } = {}
  ): Promise<ActivityLogResponse> {
    return this.api.get<ActivityLogResponse>(
      `/activity${this.toQuery(filters)}`
    );
  }

  listEntityActivity(
    agencyId: string,
    entityType: string,
    entityId: string,
    filters: ActivityLogFilters = {}
  ): Promise<ActivityLogResponse> {
    return this.api.get<ActivityLogResponse>(
      `/agency/${encodeURIComponent(agencyId)}/activity/${encodeURIComponent(
        entityType
      )}/${encodeURIComponent(entityId)}${this.toQuery(filters)}`
    );
  }

  private toQuery(filters: ActivityLogFilters & { agencyId?: string }): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  }
}
